import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Minimal typing for the Web Speech API (not in the standard DOM lib). Browser
 * support is uneven (Chrome/Edge/Android — the PWA's target; Safari partial), so
 * callers only expose the mic when `supported` is true.
 */
export interface SpeechResultEvent {
    results: {
        length: number;
        [index: number]: { 0: { transcript: string } };
    };
}
export interface SpeechRecognitionLike {
    lang: string;
    interimResults: boolean;
    continuous: boolean;
    /**
     * Chrome 151+ on desktop: let the engine infer punctuation from pauses and
     * intonation instead of waiting for a spoken "Punkt". It also capitalizes
     * the word after an inferred full stop, which is the half we are really
     * after. Optional because no other engine has it — see `toggle`.
     */
    unspokenPunctuation?: boolean;
    start(): void;
    stop(): void;
    onresult: ((event: SpeechResultEvent) => void) | null;
    onend: (() => void) | null;
    onerror: (() => void) | null;
}
export type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export function getSpeechRecognition(): SpeechRecognitionCtor | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const speechWindow = window as unknown as {
        SpeechRecognition?: SpeechRecognitionCtor;
        webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    return (
        speechWindow.SpeechRecognition ??
        speechWindow.webkitSpeechRecognition ??
        null
    );
}

/** German is the app's recording language (see the date/format convention). */
const DEFAULT_LANG = 'de-DE';

export interface Dictation {
    /** True when this browser exposes the Web Speech API. */
    supported: boolean;
    /** True while a recognition session is running. */
    listening: boolean;
    /**
     * Toggle dictation: start listening (calling `onText` with the final,
     * trimmed transcript when a phrase is recognized) or stop if already running.
     */
    toggle: (onText: (text: string) => void) => void;
    /** Stop any running recognition (no-op when idle). */
    stop: () => void;
}

/**
 * Speech-to-text as a reusable hook. One recognition runs at a time; the caller
 * decides where the recognized text goes (insert at cursor, append, …).
 */
export function useDictation(lang: string = DEFAULT_LANG): Dictation {
    const ctor = useMemo(() => getSpeechRecognition(), []);
    const supported = Boolean(ctor);
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

    // Stop a running session if the component unmounts mid-dictation.
    useEffect(() => {
        return () => {
            recognitionRef.current?.stop();
        };
    }, []);

    const stop = useCallback(() => {
        recognitionRef.current?.stop();
    }, []);

    const toggle = useCallback(
        (onText: (text: string) => void) => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
                return;
            }
            if (!ctor) {
                return;
            }
            const recognition = new ctor();
            recognition.lang = lang;
            recognition.interimResults = false;
            recognition.continuous = false;
            // Harmless where unknown (just a property nobody reads), and
            // unavailable on Android — there `capitalizeSentences` covers it.
            recognition.unspokenPunctuation = true;
            recognition.onresult = (event) => {
                let text = '';
                for (let i = 0; i < event.results.length; i++) {
                    text += event.results[i][0].transcript;
                }
                onText(text.trim());
            };
            recognition.onend = () => {
                setListening(false);
                recognitionRef.current = null;
            };
            recognition.onerror = () => {
                setListening(false);
                recognitionRef.current = null;
            };
            recognitionRef.current = recognition;
            setListening(true);
            recognition.start();
        },
        [ctor, lang]
    );

    return { supported, listening, toggle, stop };
}
